/**
 * RowFiltersPanel Component
 * Konfiguriši filtere za redove prije importa
 * Ekvivalent XPath filtera iz WP All Import
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Alert,
  Autocomplete,
  Divider,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { RowFilter, RowFilterOperator } from '../types';
import { applyRowFilters } from '../utils/importUtils';

interface RowFiltersPanelProps {
  rowFilters: RowFilter[];
  onRowFiltersChange: (filters: RowFilter[]) => void;
  availableFields: string[];
  sampleRecords: any[];
}

const OPERATOR_LABELS: Record<RowFilterOperator, string> = {
  equals: 'jednako (=)',
  not_equals: 'nije jednako (!=)',
  contains: 'sadrzi',
  not_contains: 'ne sadrzi',
  starts_with: 'pocinje sa',
  ends_with: 'zavrsava sa',
  in: 'jedno od (lista)',
  not_in: 'nije jedno od (lista)',
  greater_than: 'vece od (>)',
  less_than: 'manje od (<)',
  is_empty: 'prazno',
  is_not_empty: 'nije prazno',
};

const NO_VALUE_OPERATORS: RowFilterOperator[] = ['is_empty', 'is_not_empty'];

export function RowFiltersPanel({
  rowFilters,
  onRowFiltersChange,
  availableFields,
  sampleRecords,
}: RowFiltersPanelProps) {
  const [newField, setNewField] = useState('');
  const [newOperator, setNewOperator] = useState<RowFilterOperator>('not_in');
  const [newValue, setNewValue] = useState('');

  const handleAddFilter = () => {
    if (!newField) return;
    if (!NO_VALUE_OPERATORS.includes(newOperator) && !newValue) return;

    const filter: RowFilter = {
      id: `filter_${Date.now()}`,
      field: newField,
      operator: newOperator,
      value: NO_VALUE_OPERATORS.includes(newOperator) ? '' : newValue,
    };

    onRowFiltersChange([...rowFilters, filter]);
    setNewField('');
    setNewValue('');
  };

  const handleRemoveFilter = (id: string) => {
    onRowFiltersChange(rowFilters.filter(f => f.id !== id));
  };

  // Calculate how many records pass the filters
  const totalRecords = sampleRecords.length;
  const filteredCount = rowFilters.length > 0
    ? applyRowFilters(sampleRecords, rowFilters).length
    : totalRecords;
  const excludedCount = totalRecords - filteredCount;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterIcon fontSize="small" /> Filteri redova
        {rowFilters.length > 0 && (
          <Chip
            size="small"
            label={`${rowFilters.length} aktivan`}
            color="primary"
          />
        )}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Filtriraj redove iz feeda prije importa (AND logika - svi uslovi moraju biti ispunjeni)
      </Typography>

      {/* Existing filters */}
      {rowFilters.map(filter => (
        <Box
          key={filter.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            p: 1,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Chip label={filter.field} size="small" color="info" />
          <Chip label={OPERATOR_LABELS[filter.operator]} size="small" variant="outlined" />
          {!NO_VALUE_OPERATORS.includes(filter.operator) && (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {filter.value}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => handleRemoveFilter(filter.id)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      {/* Add new filter */}
      <Divider sx={{ my: 1.5 }} />
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={4}>
          <Autocomplete
            size="small"
            freeSolo
            options={availableFields}
            value={newField}
            onInputChange={(_, v) => setNewField(v)}
            renderInput={(params) => (
              <TextField {...params} label="Polje" placeholder="npr. klasa" />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl size="small" fullWidth>
            <InputLabel>Operator</InputLabel>
            <Select
              value={newOperator}
              onChange={(e) => setNewOperator(e.target.value as RowFilterOperator)}
              label="Operator"
            >
              {Object.entries(OPERATOR_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {!NO_VALUE_OPERATORS.includes(newOperator) && (
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Vrijednost"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder={newOperator === 'in' || newOperator === 'not_in' ? '1,1/7,1BAT' : ''}
              helperText={newOperator === 'in' || newOperator === 'not_in' ? 'zarezom razdvojene' : ''}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddFilter(); }}
            />
          </Grid>
        )}
        <Grid item xs={12} md={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddFilter}
            disabled={!newField || (!NO_VALUE_OPERATORS.includes(newOperator) && !newValue)}
            fullWidth
          >
            Dodaj
          </Button>
        </Grid>
      </Grid>

      {/* Filter results preview */}
      {rowFilters.length > 0 && (
        <Alert
          severity={excludedCount > 0 ? 'warning' : 'info'}
          sx={{ mt: 1.5 }}
          icon={<FilterIcon />}
        >
          <strong>{filteredCount}</strong> / {totalRecords} zapisa prolazi filtere
          {excludedCount > 0 && (
            <> ({excludedCount} iskljuceno)</>
          )}
        </Alert>
      )}
    </Paper>
  );
}
